import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  FaHome,
  FaUser,
  FaUsers,
  FaDollarSign,
  FaCalendarAlt,
  FaFileContract,
  FaFileSignature,
  FaExclamationTriangle,
  FaCheckCircle
} from 'react-icons/fa';
import style from './RentalCard.module.css';
import CancelRentalModal from '../CancelRentalModal/CancelRentalModal';
import {
  reactivateRental,
  deleteRental,
  fetchActiveRentals,
  reactivateAdminRental,
  deleteAdminRental,
  fetchActiveAdminRentals,
  fetchRentalOverview
} from '../../Redux/Actions/actions';
import {
  RENTAL_TYPE,
  getDetailPath
} from '../../Views/RentalManagement/utils/rentalTypeHelpers';

const RentalCard = ({ rental, type = RENTAL_TYPE.REGULAR }) => {
  const dispatch = useDispatch();
  const isAdmin = type === RENTAL_TYPE.ADMIN;
  const [showModal, setShowModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Prevenir scroll del body cuando el modal está abierto
  React.useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    if (showModal) {
      html.style.overflow = 'hidden';
      html.style.height = '100%';
      body.style.overflow = 'hidden';
      body.style.height = '100%';
    } else {
      html.style.overflow = 'unset';
      html.style.height = 'auto';
      body.style.overflow = 'unset';
      body.style.height = 'auto';
    }

    return () => {
      html.style.overflow = 'unset';
      html.style.height = 'auto';
      body.style.overflow = 'unset';
      body.style.height = 'auto';
    };
  }, [showModal]);
  
  // Validar que tenemos datos
  if (!rental || !rental.rental) {
    return (
      <div className={style.card}>
        <div className={style.noData}>
          <p>No hay datos disponibles del contrato</p>
        </div>
      </div>
    );
  }
  
  // Extraer datos de la propiedad y del contrato
  const property = rental; // Datos de la propiedad
  const contract = rental.rental; // Datos del contrato de alquiler

  // Valor inicial pactado del contrato (antes de ajustes).
  const initialRentValue =
    contract.rentValue ?? contract.currentRentValue ?? property.price ?? 0;

  // Última cuota efectivamente pagada embebida en el contrato activo.
  // Si no está, se mantiene fallback defensivo a listas ya presentes en memoria.
  const getLastPaidPayment = () => {
    const candidates = [
      contract.lastPaidPayment,
      contract.lastPayment,
      property.lastPayment,
      property.lastPaidPayment,
    ].filter(Boolean);
    if (candidates.length > 0) return candidates[0];

    const lists = [contract.payments, property.payments].filter(Array.isArray);
    for (const list of lists) {
      const paid = list.filter(
        (p) => (p.paymentStatus || p.status) === 'paid'
      );
      if (paid.length > 0) {
        return paid.reduce((latest, p) => {
          const a = Number(p.paymentNumber ?? 0);
          const b = Number(latest.paymentNumber ?? 0);
          return a >= b ? p : latest;
        }, paid[0]);
      }
    }
    return null;
  };

  const lastPaidPayment = getLastPaidPayment();
  const lastPaidAmount = lastPaidPayment
    ? lastPaidPayment.amountPaid ??
      lastPaidPayment.totalAmount ??
      lastPaidPayment.rentAmount
    : null;
  // Si no hay datos de la cuota paga, mostramos el valor actual del contrato.
  const currentPaidValue =
    lastPaidAmount != null
      ? lastPaidAmount
      : contract.currentRentValue ?? contract.rentValue ?? property.price ?? 0;
  const lastPaidDate =
    lastPaidPayment?.paymentDate || lastPaidPayment?.paidDate || null;


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-AR');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'expired': return 'danger';
      case 'terminated': return 'warning';
      default: return 'info';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Activo';
      case 'expired': return 'Vencido';
      case 'terminated': return 'Terminado';
      case 'pending': return 'Pendiente';
      default: return status;
    }
  };

  // Calcular días hasta el vencimiento
  const daysToExpiry = () => {
    const today = new Date();
    const expiryDate = new Date(rental.rental?.contractEnd || rental.rental?.endDate);
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysLeft = daysToExpiry();
  const isExpiringSoon = daysLeft <= 90 && daysLeft > 0;
  const propertyReference =
    contract.propertyReference || rental.propertyReference || 'Sin referencia';
  const ownerBankAccount =
    contract.ownerBankAccount || rental.ownerBankAccount || '';
  const ownerBankAlias =
    contract.ownerBankAlias || rental.ownerBankAlias || '';
  const ownerBankText =
    ownerBankAccount || ownerBankAlias
      ? `${ownerBankAccount || '—'}${ownerBankAlias ? ` / ${ownerBankAlias}` : ''}`
      : 'No disponible';
  const tenantsCount = Array.isArray(contract.tenants)
    ? contract.tenants.length
    : Array.isArray(rental.tenants)
    ? rental.tenants.length
    : 0;

  // Determinar el estado del contrato: priorizar el campo del backend.
  const contractStatus =
    contract.contractStatus || (rental.cerrado?.cierre ? 'active' : 'pending');
  const isTerminated = contractStatus === 'terminated';
  const canCancel = contractStatus === 'active' || contractStatus === 'pending';

  const refreshList = () => {
    dispatch(fetchRentalOverview({ type: 'all', status: 'active' })).catch(() => {});
    dispatch(fetchActiveRentals({ status: 'active' })).catch(() => {});
    if (isAdmin) dispatch(fetchActiveAdminRentals({ status: 'active' })).catch(() => {});
  };

  const handleReactivate = async () => {
    if (!window.confirm('¿Reactivar este contrato cancelado?')) return;
    setActionLoading(true);
    try {
      if (isAdmin) {
        await dispatch(reactivateAdminRental(contract.id));
      } else {
        await dispatch(reactivateRental(contract.id));
      }
      toast.success('Contrato reactivado correctamente.');
      refreshList();
    } catch (error) {
      const errorCode = error?.response?.data?.errorCode;
      const message = error?.response?.data?.message || error?.message || 'Error al reactivar.';
      if (errorCode === 'PROPERTY_HAS_ACTIVE_RENTAL') {
        const activeRentalId = error?.response?.data?.data?.activeRentalId;
        toast.error(
          ({ closeToast }) => (
            <div>
              <div>{message}</div>
              {activeRentalId && (
                <Link
                  to={`/alquileres/contrato/${activeRentalId}`}
                  onClick={closeToast}
                  style={{ color: '#fff', textDecoration: 'underline', display: 'inline-block', marginTop: 6 }}
                >
                  Ir al contrato activo
                </Link>
              )}
            </div>
          ),
          { autoClose: false }
        );
      } else if (errorCode === 'RENTAL_NOT_TERMINATED') {
        toast.info('El contrato no estaba cancelado.');
      } else {
        toast.error(message);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar el contrato? Esta acción NO se puede deshacer y solo es válida si no tiene cuotas registradas.')) return;
    setActionLoading(true);
    try {
      if (isAdmin) {
        await dispatch(deleteAdminRental(contract.id));
      } else {
        await dispatch(deleteRental(contract.id));
      }
      toast.success('Contrato eliminado.');
      refreshList();
    } catch (error) {
      const errorCode = error?.response?.data?.errorCode;
      const message = error?.response?.data?.message || error?.message || 'Error al eliminar.';
      if (errorCode === 'RENTAL_HAS_MOVEMENTS' || errorCode === 'ADMIN_RENTAL_HAS_MOVEMENTS') {
        toast.warn(
          'No se puede eliminar: el contrato tiene cuotas con movimientos. Usá "Cancelar" para conservar el historial.',
          { autoClose: 7000 }
        );
      } else {
        toast.error(message);
      }
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className={`${style.card} ${isExpiringSoon ? style.expiring : ''}`}>
      <div className={style.cardHeader}>
        <div className={style.propertyInfo}>
          <FaHome className={style.propertyIcon} />
          <div>
            <h3 className={style.propertyTitle}>
              {isAdmin && (
                <span
                  style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: 999,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                    background: '#fef3c7',
                    color: '#92400e',
                    marginRight: 8,
                    verticalAlign: 'middle'
                  }}
                  title="Solo Administración"
                >
                  Admin
                </span>
              )}
              {rental.title || `Propiedad ID: ${rental.id}`}
            </h3>
            <p className={style.propertyAddress}>
              {rental.street} {rental.number}, {rental.locality}, {rental.province}
            </p>
          </div>
        </div>
        
        <div className={`${style.status} ${style[getStatusColor(contractStatus)]}`}>
          {contractStatus === 'active' && <FaCheckCircle />}
          {contractStatus === 'expired' && <FaExclamationTriangle />}
          {getStatusText(contractStatus)}
        </div>
      </div>

      {(() => {
        // Solo mostramos el estado de firma si el backend envía la info.
        // (Los listados que no incluyen `hasSignedContract` no muestran badge
        // para no marcar todo como "sin firmar" por defecto.)
        const hasInfo =
          contract.hasSignedContract !== undefined ||
          Array.isArray(contract.documentRent);
        if (!hasInfo) return null;

        const signed =
          Boolean(contract.hasSignedContract) &&
          Array.isArray(contract.documentRent) &&
          contract.documentRent.length > 0;
        return (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              margin: '0 0 8px',
              padding: '2px 10px',
              borderRadius: 999,
              fontSize: '0.7rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              background: signed ? '#dcfce7' : '#fef3c7',
              color: signed ? '#166534' : '#92400e'
            }}
            title={
              signed
                ? 'Contrato firmado y documentado'
                : 'El contrato aún no tiene el documento firmado cargado'
            }
          >
            {signed ? 'Contrato firmado' : 'Contrato sin firmar'}
          </div>
        );
      })()}

      <div className={style.badgesRow}>
        <span className={style.badgeItem}>Contrato #{contract.id}</span>
        <span className={style.badgeItem}>Inquilinos: {tenantsCount}</span>
        <span className={style.badgeItem}>
          Ajuste: {(contract.adjustmentType || 'ICL').toUpperCase()} / {contract.adjustmentFrequency || 12} meses
        </span>
      </div>

      <div className={style.cardContent}>
        <div className={style.infoGrid}>
          <div className={style.infoItem}>
            <FaUser className={style.infoIcon} />
            <div>
              <span className={style.infoLabel}>Propietario</span>
              <span className={style.infoValue}>
                {rental.ownerName || 'Sin asignar'}
              </span>
            </div>
          </div>

          <div className={style.infoItem}>
            <FaUser className={style.infoIcon} />
            <div>
              <span className={style.infoLabel}>Agente a Cargo</span>
              <span className={style.infoValue}>
                {rental.seller?.name && rental.seller?.last_name 
                  ? `${rental.seller.name} ${rental.seller.last_name}`
                  : rental.sellerName || 'Sin asignar'}
              </span>
            </div>
          </div>

          <div className={style.infoItem}>
            <FaDollarSign className={style.infoIcon} />
            <div>
              <span className={style.infoLabel}>Valor Inicial</span>
              <span className={style.infoValue}>
                {formatCurrency(initialRentValue)}
                <span style={{fontSize: '0.8em', color: '#6b7280'}}> {rental.rental?.currency || rental.currency}</span>
              </span>
            </div>
          </div>

          <div className={style.infoItem}>
            <FaDollarSign className={style.infoIcon} />
            <div>
              <span className={style.infoLabel}>Alquiler Actual</span>
              <span className={style.infoValue}>
                {formatCurrency(rental.rental?.currentRentValue || rental.rental?.rentValue || rental.price || 0)}
                <span style={{fontSize: '0.8em', color: '#6b7280'}}> {rental.rental?.currency || rental.currency}</span>
              </span>
            </div>
          </div>

          <div className={style.infoItem}>
            <FaDollarSign className={style.infoIcon} />
            <div>
              <span className={style.infoLabel}>Última Cuota Paga</span>
              <span className={style.infoValue}>
                {formatCurrency(currentPaidValue)}
                <span style={{fontSize: '0.8em', color: '#6b7280'}}> {rental.rental?.currency || rental.currency}</span>
                {lastPaidPayment?.paymentNumber != null && (
                  <span style={{fontSize: '0.75em', color: '#6b7280', display: 'block'}}>
                    Cuota #{lastPaidPayment.paymentNumber}
                    {lastPaidDate ? ` · ${formatDate(lastPaidDate)}` : ''}
                  </span>
                )}
                {!lastPaidPayment && (
                  <span style={{fontSize: '0.72em', color: '#9ca3af', display: 'block'}}>
                    (valor actual del contrato)
                  </span>
                )}
              </span>
            </div>
          </div>

          <div className={style.infoItem}>
            <FaCalendarAlt className={style.infoIcon} />
            <div>
              <span className={style.infoLabel}>Inicio Contrato</span>
              <span className={style.infoValue}>
                {rental.rental?.contractStart ? formatDate(rental.rental.contractStart) : 'No definido'}
              </span>
            </div>
          </div>

          <div className={style.infoItem}>
            <FaCalendarAlt className={style.infoIcon} />
            <div>
              <span className={style.infoLabel}>Fin Contrato</span>
              <span className={style.infoValue}>
                {rental.rental?.contractEnd ? formatDate(rental.rental.contractEnd) : 'No definido'}
              </span>
              {isExpiringSoon && (
                <span className={style.expiryWarning}>
                  ({daysLeft} días restantes)
                </span>
              )}
            </div>
          </div>

          <div className={style.infoItem}>
            <FaCalendarAlt className={style.infoIcon} />
            <div>
              <span className={style.infoLabel}>Día de Pago</span>
              <span className={style.infoValue}>
                Día {rental.rental?.paymentDueDay || 10} de cada mes
              </span>
            </div>
          </div>

          <div className={style.infoItem}>
            <FaUsers className={style.infoIcon} />
            <div>
              <span className={style.infoLabel}>Inquilinos</span>
              <span className={style.infoValue}>{tenantsCount}</span>
            </div>
          </div>

          <div className={style.infoItem}>
            <FaHome className={style.infoIcon} />
            <div>
              <span className={style.infoLabel}>Tipo</span>
              <span className={style.infoValue}>
                {rental.propertyType || 'No especificado'}
              </span>
            </div>
          </div>

          <div className={style.infoItem}>
            <FaHome className={style.infoIcon} />
            <div>
              <span className={style.infoLabel}>Referencia</span>
              <span className={style.infoValue}>{propertyReference}</span>
            </div>
          </div>

          <div className={style.infoItem}>
            <FaUser className={style.infoIcon} />
            <div>
              <span className={style.infoLabel}>CBU / Alias</span>
              <span className={style.infoValue}>{ownerBankText}</span>
            </div>
          </div>
        </div>

        {/* Información adicional */}
        <div className={style.additionalInfo}>
          {rental.rental?.contractStart && rental.rental?.contractEnd && (
            <div className={style.contractPeriod}>
              <span>Contrato: {formatDate(rental.rental.contractStart)} - {formatDate(rental.rental.contractEnd)}</span>
            </div>
          )}
          
          {(rental.rental?.adjustmentType || rental.rental?.adjustmentFrequency) && (
            <div className={style.adjustmentInfo}>
              <span>Ajustes: {(rental.rental.adjustmentType || 'ICL').toUpperCase()} cada {rental.rental.adjustmentFrequency || 12} meses</span>
            </div>
          )}
          
          {/* Información de la propiedad */}
          <div className={style.propertyDetails}>
            <span>📍 {rental.environments} amb. | {rental.rooms} dorm. | {rental.bathrooms} baños</span>
          </div>
          
          {/* Estado del inmueble */}
          <div className={style.propertyState}>
            <span>Estado: {rental.propertyState || 'No especificado'}</span>
          </div>

          <div className={style.ownerInfo}>
            <span>CBU / Alias: {ownerBankText}</span>
          </div>
        </div>
      </div>

      <div className={style.accessGrid}>
        <Link to={getDetailPath(contract.id, type)} className={style.accessLink}>
          <FaFileContract /> Abrir contrato
        </Link>
        <Link to={getDetailPath(contract.id, type, 'pagos')} className={style.accessLink}>
          <FaDollarSign /> Pagos
        </Link>
        <Link to={getDetailPath(contract.id, type, 'inquilinos')} className={style.accessLink}>
          <FaUsers /> Inquilinos
        </Link>
        <Link to={getDetailPath(contract.id, type, 'ajustes')} className={style.accessLink}>
          <FaFileSignature /> Ajustes
        </Link>
      </div>

      <div className={style.cardActions}>
        <button
          className={`${style.actionButton} ${style.secondaryAction}`}
          onClick={() => setShowModal(true)}
        >
          Ficha rápida
        </button>
        {canCancel && (
          <button
            type="button"
            className={`${style.actionButton} ${style.dangerButton}`}
            onClick={() => setShowCancelModal(true)}
            disabled={actionLoading}
          >
            Cancelar Contrato
          </button>
        )}
        {isTerminated && (
          <button
            type="button"
            className={`${style.actionButton} ${style.successButton}`}
            onClick={handleReactivate}
            disabled={actionLoading}
          >
            {actionLoading ? 'Procesando...' : 'Reactivar'}
          </button>
        )}
        <button
          type="button"
          className={`${style.actionButton} ${style.deleteButton}`}
          onClick={handleDelete}
          disabled={actionLoading}
          title="Solo permitido si el contrato no tiene cuotas registradas"
        >
          Eliminar
        </button>
      </div>

      {/* Modal de detalles */}
      {showModal && createPortal(
        <div 
          className={style.modalOverlay} 
          onClick={() => setShowModal(false)}
        >
          <div className={style.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={style.modalHeader}>
              <h2>Detalles del Contrato de Alquiler</h2>
              <button 
                className={style.closeButton}
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className={style.modalBody}>
              {/* Información del contrato */}
              <section className={style.section}>
                <h3>Información del Contrato</h3>
                <div className={style.detailGrid}>
                  <div className={style.detailItem}>
                    <strong>ID del Contrato:</strong> {contract.id}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Estado:</strong> 
                    <span className={`${style.status} ${style[getStatusColor(contract.contractStatus)]}`}>
                      {getStatusText(contract.contractStatus)}
                    </span>
                  </div>
                  <div className={style.detailItem}>
                    <strong>Duración:</strong> {contract.contractDuration} meses
                  </div>
                  <div className={style.detailItem}>
                    <strong>Activo:</strong> {contract.isActive ? 'Sí' : 'No'}
                  </div>
                </div>
                {isTerminated && (contract.terminationDate || contract.terminationReason) && (
                  <div className={style.detailGrid} style={{ marginTop: 8 }}>
                    {contract.terminationDate && (
                      <div className={style.detailItem}>
                        <strong>Fecha de cancelación:</strong> {formatDate(contract.terminationDate)}
                      </div>
                    )}
                    {contract.terminationReason && (
                      <div className={style.detailItem}>
                        <strong>Motivo:</strong> {contract.terminationReason}
                      </div>
                    )}
                  </div>
                )}
              </section>

              {/* Información financiera */}
              <section className={style.section}>
                <h3>Información Financiera</h3>
                <div className={style.detailGrid}>
                  <div className={style.detailItem}>
                    <strong>Valor inicial:</strong> 
                    {formatCurrency(contract.rentValue)} {contract.currency}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Valor actual:</strong> 
                    {formatCurrency(contract.currentRentValue)} {contract.currency}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Depósito:</strong> 
                    {formatCurrency(contract.depositAmount)} {contract.depositCurrency}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Expensas:</strong> 
                    {contract.expensesIncluded ? 'Incluidas' : 'No incluidas'}
                    {!contract.expensesIncluded && contract.expensesAmount && 
                      ` (${formatCurrency(contract.expensesAmount)} ${contract.currency})`
                    }
                  </div>
                  <div className={style.detailItem}>
                    <strong>Día de vencimiento:</strong> {contract.paymentDueDay} de cada mes
                  </div>
                  <div className={style.detailItem}>
                    <strong>Interés por mora:</strong> {contract.lateInterestRate}% diario
                  </div>
                  <div className={style.detailItem}>
                    <strong>Período de gracia:</strong> {contract.gracePeriodDays} días
                  </div>
                </div>
              </section>

              {/* Fechas importantes */}
              <section className={style.section}>
                <h3>Fechas del Contrato</h3>
                <div className={style.detailGrid}>
                  <div className={style.detailItem}>
                    <strong>Inicio:</strong> {formatDate(contract.contractStart)}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Fin:</strong> {formatDate(contract.contractEnd)}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Próximo ajuste:</strong> {formatDate(contract.nextAdjustmentDate)}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Fecha de creación:</strong> {formatDate(contract.createdAt)}
                  </div>
                </div>
              </section>

              {/* Ajustes */}
              <section className={style.section}>
                <h3>Configuración de Ajustes</h3>
                <div className={style.detailGrid}>
                  <div className={style.detailItem}>
                    <strong>Tipo de ajuste:</strong> {contract.adjustmentType}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Frecuencia:</strong> cada {contract.adjustmentFrequency} meses
                  </div>
                  {contract.adjustmentPercentage && (
                    <div className={style.detailItem}>
                      <strong>Porcentaje fijo:</strong> {contract.adjustmentPercentage}%
                    </div>
                  )}
                </div>
              </section>

              {/* Propietario */}
              <section className={style.section}>
                <h3>Información del Propietario</h3>
                <div className={style.detailGrid}>
                  <div className={style.detailItem}>
                    <strong>Nombre:</strong> {contract.ownerName || 'No especificado'}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Teléfono:</strong> {contract.ownerPhone || 'No especificado'}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Email:</strong> {contract.ownerEmail || 'No especificado'}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Cuenta bancaria:</strong> {contract.ownerBankAccount || 'No especificado'}
                  </div>
                  <div className={style.detailItem}>
                    <strong>CBU / Alias:</strong>{' '}
                    {contract.ownerBankAccount || contract.ownerBankAlias
                      ? `${contract.ownerBankAccount || '—'}${
                          contract.ownerBankAlias ? ` / ${contract.ownerBankAlias}` : ''
                        }`
                      : 'No especificado'}
                  </div>
                </div>
              </section>

              {/* Agente/Seller a Cargo */}
              <section className={style.section}>
                <h3>Agente a Cargo de la Propiedad</h3>
                <div className={style.detailGrid}>
                  <div className={style.detailItem}>
                    <strong>Nombre:</strong> {
                      rental.seller?.name && rental.seller?.last_name
                        ? `${rental.seller.name} ${rental.seller.last_name}`
                        : rental.sellerName || 'No especificado'
                    }
                  </div>
                  {rental.seller?.mail && (
                    <div className={style.detailItem}>
                      <strong>Email:</strong> {rental.seller.mail}
                    </div>
                  )}
                  {rental.seller?.phone && (
                    <div className={style.detailItem}>
                      <strong>Teléfono:</strong> {rental.seller.phone}
                    </div>
                  )}
                  {rental.seller?.type && (
                    <div className={style.detailItem}>
                      <strong>Tipo:</strong> {rental.seller.type}
                    </div>
                  )}
                </div>
              </section>

              {/* Propiedad */}
              <section className={style.section}>
                <h3>Información de la Propiedad</h3>
                <div className={style.detailGrid}>
                  <div className={style.detailItem}>
                    <strong>ID de propiedad:</strong> {contract.propertyId}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Tipo:</strong> {property.propertyType}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Referencia:</strong> {propertyReference}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Dirección:</strong> {property.street} {property.number}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Localidad:</strong> {property.locality}, {property.province}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Ambientes:</strong> {property.environments}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Dormitorios:</strong> {property.rooms}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Baños:</strong> {property.bathrooms}
                  </div>
                  <div className={style.detailItem}>
                    <strong>Estado:</strong> {property.propertyState}
                  </div>
                </div>
              </section>

              {/* Notas y cláusulas */}
              {(contract.notes || contract.specialClauses) && (
                <section className={style.section}>
                  <h3>Notas y Cláusulas Especiales</h3>
                  {contract.notes && (
                    <div className={style.detailItem}>
                      <strong>Notas:</strong>
                      <p>{contract.notes}</p>
                    </div>
                  )}
                  {contract.specialClauses && (
                    <div className={style.detailItem}>
                      <strong>Cláusulas especiales:</strong>
                      <p>{contract.specialClauses}</p>
                    </div>
                  )}
                </section>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {showCancelModal && (
        <CancelRentalModal
          rentalId={contract.id}
          propertyTitle={rental.title || `${rental.street || ''} ${rental.number || ''}`.trim()}
          type={type}
          onClose={() => setShowCancelModal(false)}
          onSuccess={refreshList}
        />
      )}
    </div>
  );
};

export default RentalCard;